// Types
import type React from 'react';
import { useEffect, useState } from 'react';
import CheckableSelect from 'components/CheckableSelect/CheckableSelect';
import { supportsRollingDays } from 'constants/DynamicAvailability.constants';
import _ from 'lodash';
// Utils
import { hourMinuteToSecond, secondToHourMinute } from 'utils/timeConverter';
import {
  EDynamicAvailabilityType,
  EFullWeekDays,
  ETimeScope,
  type TDynamicAvailability,
} from '@timeedit/types/lib/types';
// Components
import { TEDrawerSection, TEInput } from '@timeedit/ui-components';
// i18N
import intl from '../../../../i18n/intl';

const language: any = intl.messages;
interface Props {
  dynamicAvailability?: TDynamicAvailability;
  onChange: (updatedValues: Record<string, any>) => void;
}

const UNAVAILABILITY_HOURS_WEEKLY = 'HOURS_WEEKLY';

// i18n key prefix for the time-interval options of each availability type
const TIME_INTERVAL_LABEL_PREFIX: Record<EDynamicAvailabilityType, string> = {
  [EDynamicAvailabilityType.FREE_TIME]: 'UNAVAILABILITY',
  [EDynamicAvailabilityType.TOTAL_HOURS]: 'MAX_AVAILABLE',
  [EDynamicAvailabilityType.CONSECUTIVE_HOURS]: 'MAX_AVAILABLE',
  [EDynamicAvailabilityType.TOTAL_RESERVATIONS]: 'MAX_RESERVATIONS',
};

const Properties: React.FC<Props> = ({ dynamicAvailability, onChange }) => {
  const [timeConditionEnabled, setTimeConditionEnabled] = useState(true);
  const [scopeType, setScopeType] = useState<string | undefined>();

  useEffect(() => {
    if (!dynamicAvailability) return;
    const {
      scopeType,
      minBreakLength,
      scopeBeginTime,
      scopeEndTime,
      dynamicAvailabilityType,
    } = dynamicAvailability;
    // If time condition disabled, default scope begin time, scope end time to 00h00 and 23h00
    if (scopeBeginTime === 0 && scopeEndTime === 82800) {
      setTimeConditionEnabled(false);
    }

    // Set scope type for Unavailability weekly
    if (
      dynamicAvailabilityType === EDynamicAvailabilityType.FREE_TIME &&
      scopeType === ETimeScope.WEEKLY &&
      (minBreakLength !== 72000 ||
        scopeBeginTime !== 14400 ||
        scopeEndTime !== 86400)
    ) {
      setScopeType(UNAVAILABILITY_HOURS_WEEKLY);
    } else {
      setScopeType(scopeType);
    }
  }, [dynamicAvailability]);

  if (!dynamicAvailability) return null;

  // Only show time interval for Unavailability, Max available hours and Max available reservations
  const showTimeInterval = [
    EDynamicAvailabilityType.FREE_TIME,
    EDynamicAvailabilityType.TOTAL_HOURS,
    EDynamicAvailabilityType.TOTAL_RESERVATIONS,
  ].includes(dynamicAvailability.dynamicAvailabilityType);

  const showRollingDays = supportsRollingDays(
    dynamicAvailability.dynamicAvailabilityType,
    scopeType
  );

  // Only show number of days for Unavailability weekly
  const showNumberOfDays =
    dynamicAvailability.dynamicAvailabilityType ===
      EDynamicAvailabilityType.FREE_TIME && scopeType === ETimeScope.WEEKLY;

  // Hours per week option only for Unavailability weekly
  const timeIntervalOptions =
    dynamicAvailability.dynamicAvailabilityType ===
    EDynamicAvailabilityType.FREE_TIME
      ? [...Object.values(ETimeScope), UNAVAILABILITY_HOURS_WEEKLY]
      : Object.values(ETimeScope);

  // Only show time setting for Unavailability hours per day and hours per week
  const showTimeSetting =
    dynamicAvailability.dynamicAvailabilityType ===
      EDynamicAvailabilityType.FREE_TIME &&
    [ETimeScope.DAILY, UNAVAILABILITY_HOURS_WEEKLY].includes(scopeType);

  return (
    <TEDrawerSection label="Properties">
      <div className="section-col">
        {showTimeInterval && (
          <TEInput.Select
            label="Time interval"
            tooltip="Time interval"
            value={scopeType}
            onChange={(val) => {
              if (val === ETimeScope.WEEKLY) {
                // Default time settings for Unavailability days per week
                onChange({
                  scopeType: val,
                  minBreakLength: 72000,
                  scopeBeginTime: 14400,
                  scopeEndTime: 86400,
                });
              } else {
                // Unavailability hour per week has same scope type as day per week
                onChange({
                  scopeType:
                    val === UNAVAILABILITY_HOURS_WEEKLY
                      ? ETimeScope.WEEKLY
                      : val,
                  minBreakLength: null,
                  scopeBeginTime: null,
                  scopeEndTime: null,
                });
              }
            }}
            options={timeIntervalOptions.map((scope) => ({
              value: scope,
              label:
                language[
                  `${
                    TIME_INTERVAL_LABEL_PREFIX[
                      dynamicAvailability.dynamicAvailabilityType
                    ]
                  }_${scope}`
                ],
            }))}
            data-testid="time-interval"
          />
        )}
        <CheckableSelect
          label="Weekdays"
          tooltip="Weekdays"
          flow="row"
          value={(dynamicAvailability?.scopeWeekDays || []) as EFullWeekDays[]}
          onChange={(val) => onChange({ scopeWeekDays: val })}
          options={Object.values(EFullWeekDays).map((scope) => ({
            value: scope,
            label: _.startCase(scope.toLocaleLowerCase().slice(0, 3)),
          }))}
        />
        {showNumberOfDays && (
          <TEInput.InputNumber
            label="Number of days"
            tooltip="Number of days"
            value={dynamicAvailability?.minBreakCount}
            onChange={(val) =>
              onChange({
                minBreakCount: val,
              })
            }
            data-testid="number-of-day"
          />
        )}
        {showTimeSetting && (
          <>
            <TEInput.HoursMinuteInput
              label="Duration"
              tooltip="Duration"
              value={secondToHourMinute(dynamicAvailability?.minBreakLength)}
              onChange={(val: [number, number]) =>
                onChange({ minBreakLength: hourMinuteToSecond(val) })
              }
              minuteStep={5}
              data-testid="duration"
            />
            <TEInput.Switch
              label="Time condition"
              tooltip="Time condition"
              checked={timeConditionEnabled}
              onChange={(val) => {
                if (!val) {
                  onChange({
                    scopeBeginTime: 0,
                    scopeEndTime: 82800,
                  });
                }
                setTimeConditionEnabled(val);
              }}
              data-testid="time-condition"
            />
            <TEInput.HoursMinuteInput
              label="Start time"
              tooltip="Start time"
              value={secondToHourMinute(dynamicAvailability?.scopeBeginTime)}
              onChange={(val: [number, number]) =>
                onChange({ scopeBeginTime: hourMinuteToSecond(val) })
              }
              disabled={!timeConditionEnabled}
              minuteStep={5}
              data-testid="start-time"
            />
            <TEInput.HoursMinuteInput
              label="End time"
              tooltip="End time"
              value={secondToHourMinute(dynamicAvailability?.scopeEndTime)}
              onChange={(val: [number, number]) =>
                onChange({ scopeEndTime: hourMinuteToSecond(val) })
              }
              disabled={!timeConditionEnabled}
              minuteStep={5}
              data-testid="end-time"
            />
          </>
        )}
        {dynamicAvailability.dynamicAvailabilityType ===
          EDynamicAvailabilityType.TOTAL_HOURS && (
          <TEInput.HoursMinuteInput
            label="Duration"
            tooltip="Duration"
            value={secondToHourMinute(
              dynamicAvailability?.maxReservationLength
            )}
            onChange={(val: [number, number]) =>
              onChange({ maxReservationLength: hourMinuteToSecond(val) })
            }
            minuteStep={5}
            data-testid="duration"
          />
        )}
        {dynamicAvailability.dynamicAvailabilityType ===
          EDynamicAvailabilityType.TOTAL_RESERVATIONS && (
          <TEInput.InputNumber
            label="Reservation count"
            tooltip="Maximum number of reservations allowed within the time interval"
            min={1}
            value={dynamicAvailability?.maxReservationCount}
            onChange={(val) => onChange({ maxReservationCount: val })}
            data-testid="reservation-count"
          />
        )}
        {showRollingDays && (
          <TEInput.InputNumber
            label="Rolling days"
            tooltip="Number of consecutive days the limit applies to, rolling. 1 = per single day; 3 = per any 3 consecutive days"
            min={1}
            value={dynamicAvailability?.unitCount ?? 1}
            onChange={(val) => onChange({ unitCount: val })}
            data-testid="rolling-days"
          />
        )}
        {dynamicAvailability.dynamicAvailabilityType ===
          EDynamicAvailabilityType.CONSECUTIVE_HOURS && (
          <>
            <TEInput.HoursMinuteInput
              label="Max consecutive hours"
              tooltip="Max consecutive hours"
              value={secondToHourMinute(
                dynamicAvailability?.maxReservationLength
              )}
              onChange={(val: [number, number]) =>
                onChange({ maxReservationLength: hourMinuteToSecond(val) })
              }
              minuteStep={5}
              data-testid="max-consecutive"
            />
            <TEInput.HoursMinuteInput
              label="Minimum break"
              tooltip="Minimum break"
              value={secondToHourMinute(dynamicAvailability?.minBreakLength)}
              onChange={(val: [number, number]) =>
                onChange({ minBreakLength: hourMinuteToSecond(val) })
              }
              minuteStep={5}
              data-testid="minimum-break"
            />
          </>
        )}
      </div>
    </TEDrawerSection>
  );
};

export default Properties;
